import { addDays, addHours, addMinutes } from 'date-fns';
import SUPPORTING_DOCS from './supportingDocs';
import { Attachment, RuleProps, FieldValue } from 'types/application';
import { formatDateTime } from 'features/applicationPage/helpers';

export const SECT_PE = 'pe-details';
export const SECT_EVENT_DETAILS = 'event-details';
export const SECT_EVENT_VENUE = 'event-venue';
export const SECT_PERFORMER = 'performer';
export const SECT_DOCS = 'supporting-docs';

export const EVENT_FIELDS = [
    {
        sectionID: SECT_PE,
        sectionName: 'Public Entertainment (PE) Licence Details',
        // note: 'Please ensure that the PE Licensee is the same as the Arts Entertainment Classification Applicant.',
        fieldList: [
            {
                id: 'pe-no',
                apiID: 'PELicenceNo',
                label: 'PE Licence No.',
                type: 'text',
                placeholder: 'Eg: L/PE/XXXXXX/YYYY',
                labelNote: ' Please include /P at the end of the PE Licence No., if applicable.',
                rule: { 
                    charLength: 18,
                    pattern: /^L\/PE\/[0-9]{6}\/[0-9]{4}(\/P)?$/ 
                }
            },
            {
                id: 'pe-type',
                apiID: 'PELicenceType',
                label: 'PE Licence Type',
                type: 'dropdown',
                secondaryLabel: 'X',
                rule: {
                    required: true,
                    disabled: ({ isSingpass }: RuleProps) => isSingpass,
                    setValue: ({ isSingpass, currentValue }: RuleProps) => (isSingpass ? 'ADH' : currentValue)
                }
            },
            {
                id: 'cat1',
                apiID: 'Category1Public',
                label: 'Name of the Cat 1 Public Entertainment Licensed Establishment',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_PE}-pe-type`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_PE}-pe-type`] === 'CATE');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_PE}-pe-type`] !== 'CATE');
                        return result;
                    }
                },
                span: 12
            },
            {
                id: 'pe-start',
                apiID: 'PELicenceStartDate',
                label: 'PE Licence Start Date',
                type: 'date',
                rule: {
                    baseIds: [`${SECT_PE}-pe-no`, `${SECT_PE}-pe-end`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_PE}-pe-no`]);
                        return result;
                    },
                    revalidate: ['event-details-event-date-time', `${SECT_PE}-pe-end`],
                    customValidate: ({ currentValue, baseValues }: RuleProps) => {
                        if (
                            currentValue &&
                            baseValues?.[`${SECT_PE}-pe-end`] &&
                            new Date(currentValue as string) > new Date(baseValues[`${SECT_PE}-pe-end`] as string)
                        ) {
                            return 'PE Licence Start Date should not greater than PE Licence End Date';
                        }

                        return true;
                    }
                    // customValidate: ({ currentValue, baseValues }: RuleProps) => {
                    //     if (baseValues) {
                    //         const eventDates = baseValues['event-details-event-date-time'] as { [key: string]: FieldValue }[] | undefined;
                    //         if (eventDates) {
                    //             const minDate = eventDates.reduce((min: Date | undefined, current: { [key: string]: FieldValue }) => {
                    //                 if (!current.from) return min;
                    //                 if (!min) return current.from ? new Date(current.from as string) : undefined;
                    //                 return min < new Date(current.from as string) ? min : new Date(current.from as string);
                    //             }, undefined);

                    //             const maxDate = eventDates.reduce((max: Date | undefined, current: { [key: string]: FieldValue }) => {
                    //                 if (!current.to) return max;
                    //                 if (!max) return current.to ? new Date(current.to as string) : undefined;
                    //                 return max < new Date(current.to as string) ? max : new Date(current.to as string);
                    //             }, undefined);

                    //             const dateValue = new Date(currentValue as string);
                    //             const minCondition = Boolean(minDate && dateValue > new Date(minDate));
                    //             const mixCondition = Boolean(maxDate && dateValue < new Date(maxDate));
                    //             if (minCondition || mixCondition) {
                    //                 return 'Event Date/ Time From should fall between the PE Licence Period Start and End Date';
                    //             }
                    //         }
                    //     }
                    //     return true;
                    // }
                }
            },
            {
                id: 'pe-end',
                apiID: 'PELicenceEndDate',
                label: 'PE Licence End Date',
                type: 'date',
                rule: {
                    baseIds: [`${SECT_PE}-pe-no`, `${SECT_PE}-pe-start`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_PE}-pe-no`]);
                        return result;
                    },
                    dateLimit: ({ baseValues }: RuleProps) => {
                        const baseDate =
                            baseValues && baseValues[`${SECT_PE}-pe-start`]
                                ? new Date(baseValues[`${SECT_PE}-pe-start`] as string)
                                : new Date();
                        return { minDate: baseDate };
                    },
                    revalidate: ['event-details-event-date-time', `${SECT_PE}-pe-start`],
                    customValidate: ({ currentValue, baseValues }: RuleProps) => {
                        if (
                            currentValue &&
                            baseValues?.[`${SECT_PE}-pe-start`] &&
                            new Date(currentValue as string) < new Date(baseValues[`${SECT_PE}-pe-start`] as string)
                        ) {
                            return 'PE Licence Start Date should not greater than PE Licence End Date';
                        }

                        return true;
                    }
                }
            }
        ]
    },
    {
        sectionID: SECT_EVENT_DETAILS,
        sectionName: 'Event Details',
        fieldList: [
            {
                id: 'event-title',
                apiID: 'Title',
                label: 'Event Title',
                type: 'longText',
                rule: {
                    required: true,
                    charLength: 600,
                    customValidate: ({ currentValue }: RuleProps) => {
                        if (currentValue) {
                            const result = /(\b((https?|ftp|file):\/\/|(www\.))[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi.test(
                                currentValue as string
                            );
                            if (result) return 'URL Not Allowed in Title.';
                        }

                        return true;
                    }
                },
                span: 12
            },
            {
                id: 'event-type',
                apiID: 'IMDA-IRIS-Work-Arts-Type',
                label: 'Event Type',
                type: 'dropdown',
                rule: { required: true }
            },
            {
                id: 'event-sub-type',
                apiID: 'SubType',
                label: 'Event Sub Type',
                type: 'dropdown',
                rule: {
                    baseIds: [`${SECT_EVENT_DETAILS}-event-type`],
                    // optionGroup: ({ baseValues }: RuleProps) => {
                    //     if (baseValues && baseValues[`${SECT_PE}-event-type`]) return baseValues[`${SECT_PE}-event-type`];
                    //     return '';
                    // },
                    optionGroup: `${SECT_EVENT_DETAILS}-event-type`,
                    required: true
                }
            },
            {
                id: 'sub-type-desc',
                apiID: 'SubTypeDescription',
                label: 'Event Sub-Type Description',
                type: 'text',
                hasCharCounter: true,
                rule: {
                    baseIds: [`${SECT_EVENT_DETAILS}-event-sub-type`],
                    charLength: 100,
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_DETAILS}-event-sub-type`] === 'Others');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_DETAILS}-event-sub-type`] !== 'Others');
                        return result;
                    }
                }
            },
            {
                id: 'language',
                apiID: 'Language',
                label: 'Language',
                labelNote: ' For materials in a foreign language, English translations are to be provided.',
                type: 'dropdown',
                rule: { required: true }
            },
            {
                id: 'lang-desc',
                label: 'Language Description',
                apiID: 'LanguageDescription',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_EVENT_DETAILS}-language`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_DETAILS}-language`] === 'XX'); // XX = Others
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_DETAILS}-language`] !== 'XX');
                        return result;
                    }
                }
            },
            {
                id: 'event-desc',
                label: 'Event Description',
                apiID: 'Description',
                type: 'longText',
                span: 12,
                textRows: 4,
                hasCharCounter: true,
                rule: { required: true, charLength: 5000 }
            },
            {
                id: 'offered-online',
                label: 'Will the performance be offered online i.e. live-streamed or offered as Video-On-Demand (VOD) content?',
                apiID: '',
                type: 'radio',
                options: [
                    {
                        optID: 'Yes',
                        optLabel: 'Yes'
                    },
                    {
                        optID: 'No',
                        optLabel: 'No'
                    }
                ],
                span: 12,
                rule: { required: true }
            },
            {
                id: 'online-platform',
                label: 'Please state the online platform e.g. Zoom, Facebook, YouTube, Sistic Live that will be used.',
                type: 'checkboxGroup',
                isRowDisplay: true,
                options: [
                    {
                        optID: 'Zoom',
                        optLabel: 'Zoom'
                    },
                    {
                        optID: 'Facebook',
                        optLabel: 'Facebook'
                    },
                    {
                        optID: 'Youtube',
                        optLabel: 'YouTube'
                    },
                    {
                        optID: 'SISITICLive',
                        optLabel: 'Sistic Live'
                    },
                    {
                        optID: 'Others',
                        optLabel: 'Others'
                    }
                ],
                rule: {
                    baseIds: [`${SECT_EVENT_DETAILS}-offered-online`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_DETAILS}-offered-online`] === 'Yes');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_DETAILS}-offered-online`] !== 'Yes');
                        return result;
                    }
                }
            },

            {
                id: 'ticketed-or-free',
                label: 'Please state if the online component is ticketed or offered free',
                apiID: '',
                type: 'radio',
                options: [
                    {
                        optID: 'Ticketed',
                        optLabel: 'Ticketed'
                    },
                    {
                        optID: 'Offered Free',
                        optLabel: 'Offered Free'
                    }
                ],
                rule: {
                    baseIds: [`${SECT_EVENT_DETAILS}-offered-online`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_DETAILS}-offered-online`] === 'Yes');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_DETAILS}-offered-online`] !== 'Yes');
                        return result;
                    }
                }
            },
            {
                id: 'online-platform-others',
                label: 'Others',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_EVENT_DETAILS}-online-platform`, `${SECT_EVENT_DETAILS}-offered-online`],
                    required: ({ baseValues }: RuleProps) => {
                        const offeredOnline = Boolean(baseValues && baseValues[`${SECT_EVENT_DETAILS}-offered-online`] === 'Yes');
                        const result = Boolean(
                            baseValues && (baseValues[`${SECT_EVENT_DETAILS}-online-platform`] as string[])
                                ? (baseValues[`${SECT_EVENT_DETAILS}-online-platform`] as string[]).includes('Others')
                                : false
                        );
                        return offeredOnline && result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const offeredOnline = Boolean(baseValues && baseValues[`${SECT_EVENT_DETAILS}-offered-online`] === 'Yes');
                        const result = Boolean(
                            baseValues && (baseValues[`${SECT_EVENT_DETAILS}-online-platform`] as string[])
                                ? (baseValues[`${SECT_EVENT_DETAILS}-online-platform`] as string[]).includes('Others')
                                : false
                        );
                        return !offeredOnline || !result;
                    }
                }
            },
            {
                id: 'in-out',
                label: 'Type of Venue',
                apiID: 'IndoorOutdoor',
                type: 'radio',
                options: [
                    {
                        optID: 'Indoor',
                        optLabel: 'Indoor'
                    },
                    {
                        optID: 'Outdoor',
                        optLabel: 'Outdoor'
                    },
                    {
                        optID: 'Indoor & Outdoor',
                        optLabel: 'Indoor & Outdoor'
                    }
                ],
                rule: { required: true }
            },
            {
                id: 'event-date-time',
                label: 'Event Date & Time',
                type: 'table',
                rule: { required: true },
                labelNote: 'Event Start Date must not be less than 7 days from application date.',
                subFields: [
                    {
                        id: 'from',
                        header: 'From',
                        type: 'dateTime',
                        rule: {
                            baseIds: [`${SECT_PE}-pe-start`, `${SECT_PE}-pe-end`, `${SECT_EVENT_DETAILS}-event-date-time.idx.to`],
                            dateLimit: ({ baseValues }: RuleProps) => {
                                const baseStartDate =
                                    baseValues && baseValues[`${SECT_PE}-pe-start`]
                                        ? new Date(baseValues[`${SECT_PE}-pe-start`] as string)
                                        : new Date();
                                const baseEndDate =
                                    baseValues && baseValues[`${SECT_PE}-pe-end`] && new Date(baseValues[`${SECT_PE}-pe-end`] as string);
                                return {
                                    minDate: baseStartDate <= addDays(new Date(), 7) ? addDays(new Date(), 7) : baseStartDate,
                                    maxDate: baseEndDate
                                };
                            },
                            required: true,
                            revalidate: [`${SECT_EVENT_DETAILS}-event-date-time.idx.to`],
                            customValidate: ({ currentValue, baseValues }: RuleProps) => {
                                const peStart = baseValues && (baseValues[`${SECT_PE}-pe-start`] as string);
                                const peEnd = baseValues && (baseValues[`${SECT_PE}-pe-end`] as string);
                                if (currentValue) {
                                    const current = new Date(currentValue as string);
                                    const start = Boolean(peStart && new Date(peStart) > current);
                                    const end = Boolean(peEnd && addMinutes(addHours(new Date(peEnd), 23), 59) < current);
                                    if (start || end) {
                                        return 'Event Date/ Time From should fall between the PE Licence Period Start and End Date';
                                    }

                                    const ltDay7 = addDays(new Date(), 7) > current;
                                    const today = formatDateTime(new Date().toISOString(), 'date');
                                    if (ltDay7) return `Event Start Date/Time must be at least 7 calendar days from ${today}`;
                                }
                                const idTo =
                                    baseValues &&
                                    Object.keys(baseValues).find((key: string) => {
                                        const keySplit = key.split('.');
                                        if (keySplit.length === 3) {
                                            const baseID = `${SECT_EVENT_DETAILS}-event-date-time.idx.to`;
                                            return baseID.includes(keySplit[0]) && baseID.includes(keySplit[2]);
                                        }
                                        return false;
                                    });
                                const baseTo = baseValues && idTo && baseValues[idTo] && new Date(baseValues[idTo] as string);
                                if (baseTo && currentValue && baseTo < new Date(currentValue as string)) {
                                    return 'Date/Time From cannot be later than Date/Time To';
                                }
                                if (baseTo && currentValue && new Date(baseTo).getTime() === new Date(currentValue as string).getTime()) {
                                    return 'Date/Time From cannot be the same as Date/Time To';
                                }
                                return true;
                            }
                        }
                    },
                    {
                        id: 'to',
                        header: 'To',
                        type: 'dateTime',
                        rule: {
                            baseIds: [`${SECT_EVENT_DETAILS}-event-date-time.idx.from`, `${SECT_PE}-pe-end`],
                            dateLimit: ({ baseValues }: RuleProps) => {
                                const idFrom =
                                    baseValues &&
                                    Object.keys(baseValues).find((key: string) => {
                                        const keySplit = key.split('.');
                                        if (keySplit.length === 3) {
                                            const baseID = `${SECT_EVENT_DETAILS}-event-date-time.idx.from`;
                                            return baseID.includes(keySplit[0]) && baseID.includes(keySplit[2]);
                                        }
                                        return false;
                                    });
                                const baseStartDate = baseValues && idFrom && baseValues[idFrom] && new Date(baseValues[idFrom] as string);
                                const baseEndDate =
                                    baseValues && baseValues[`${SECT_PE}-pe-end`] && new Date(baseValues[`${SECT_PE}-pe-end`] as string);
                                return {
                                    minDate: baseStartDate,
                                    maxDate: baseEndDate
                                };
                            },
                            required: true,
                            revalidate: [`${SECT_EVENT_DETAILS}-event-date-time.idx.from`],
                            customValidate: ({ currentValue, baseValues }: RuleProps) => {
                                const peStart = baseValues && (baseValues[`${SECT_PE}-pe-start`] as string);
                                const peEnd = baseValues && (baseValues[`${SECT_PE}-pe-end`] as string);
                                if (currentValue) {
                                    const current = new Date(currentValue as string);
                                    const start = Boolean(peStart && new Date(peStart) > current);
                                    const end = Boolean(peEnd && addMinutes(addHours(new Date(peEnd), 23), 59) < current);
                                    if (start || end) {
                                        return 'Event Date/ Time To should fall between the PE Licence Period Start and End Date';
                                    }
                                }
                                const idFrom =
                                    baseValues &&
                                    Object.keys(baseValues).find((key: string) => {
                                        const keySplit = key.split('.');
                                        if (keySplit.length === 3) {
                                            const baseID = `${SECT_EVENT_DETAILS}-event-date-time.idx.from`;
                                            return baseID.includes(keySplit[0]) && baseID.includes(keySplit[2]);
                                        }
                                        return false;
                                    });
                                const baseFrom = baseValues && idFrom && baseValues[idFrom] && new Date(baseValues[idFrom] as string);

                                if (
                                    baseFrom &&
                                    currentValue &&
                                    new Date(baseFrom).getTime() === new Date(currentValue as string).getTime()
                                ) {
                                    return 'Date/Time To cannot be same as Date/Time From';
                                }
                                if (baseFrom && currentValue && baseFrom > new Date(currentValue as string)) {
                                    return 'Date/Time From cannot be later than Date/Time To';
                                }

                                return true;
                            }
                        }
                    }
                ],
                span: 12,
                buttonTitle: 'Add Date & Time'
            },
            {
                id: 'admission-fee',
                label: 'Admission Fee/Seat Price',
                apiID: 'AdmissionFee',
                type: 'text',
                rule: { required: true }
            },
            {
                id: 'crowd-size',
                label: 'Expected Crowd Size',
                labelNote: 'Please indicate the expected crowd size per day for exhibition and per show for all other event types.',
                apiID: 'CrowdSize',
                type: 'number',
                rule: { required: true }
            }
        ]
    },
    {
        sectionID: SECT_EVENT_VENUE,
        sectionName: 'Event Venue',
        fieldList: [
            {
                id: 'address-type',
                label: 'Address Type',
                type: 'dropdown',
                apiID: 'AddressType',
                rule: { required: true },
                options: [
                    {
                        optID: 'SG',
                        optLabel: 'Singapore'
                    },
                    {
                        optID: 'UNF',
                        optLabel: 'Unformatted'
                    }
                ],
                defaultValue: 'SG',
                isNextEmpty: true
            },
            {
                id: 'postal',
                label: 'Postal Code',
                apiID: 'PostalCode',
                type: 'postal',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] === 'SG');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'SG');
                        return result;
                    }
                }
            },
            {
                id: 'block-house',
                label: 'Block/House No.',
                apiID: 'BlockNumber',
                type: 'dropdown',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] === 'SG');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'SG');
                        return result;
                    }
                }
            },
            {
                id: 'street',
                label: 'Street Name',
                apiID: 'StreetName',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`, `${SECT_EVENT_VENUE}-postal`],
                    charLength: 500,
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] === 'SG');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'SG');
                        return result;
                    }
                }
            },
            {
                id: 'floor',
                label: 'Floor No.',
                apiID: 'FloorNumber',
                type: 'text',
                span: 2,
                placeholder: 'Eg: 01, B1, 01M',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    charLength: 3,
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'SG');
                        return result;
                    }
                }
            },
            {
                id: 'unit',
                label: 'Unit No.',
                apiID: 'UnitNumber',
                type: 'text',
                span: 2,
                placeholder: 'Eg: #05-01, use 01',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    charLength: 5,
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'SG');
                        return result;
                    }
                }
            },
            {
                id: 'address1',
                label: 'Address Line 1',
                apiID: 'AddressLine1',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    charLength: 60,
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] === 'UNF');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'UNF');
                        return result;
                    }
                }
            },
            {
                id: 'address2',
                label: 'Address Line 2',
                apiID: 'AddressLine2',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    charLength: 60,
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'UNF');
                        return result;
                    }
                }
            },
            {
                id: 'building',
                label: 'Building Name',
                apiID: 'BuildingName',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    charLength: 500,
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(!baseValues || !baseValues?.[`${SECT_EVENT_VENUE}-address-type`]);
                        return result;
                    }
                }
            },
            {
                id: 'location-desc',
                label: 'Description of Venue',
                apiID: 'LocationDescription',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(!baseValues || !baseValues?.[`${SECT_EVENT_VENUE}-address-type`]);
                        return result;
                    }
                }
            }
        ]
    },
    {
        sectionID: 'performer',
        sectionName: 'Performer Details',
        note: "You may choose to provide the list of performers in the table below and/or via an attachment. For performances by groups/bands, please provide the group/band name(s) and all the members' names.",
        fieldList: [
            {
                id: 'provide-performer-list',
                label: "Do you wish to provide Performer's List now?",
                apiID: '',
                type: 'radio',
                options: [
                    {
                        optID: 'Yes',
                        optLabel: 'Yes'
                    },
                    {
                        optID: 'No',
                        optLabel: 'No'
                    }
                ],
                span: 12,
                rule: { required: true }
            },
            {
                id: 'performer-list',
                label: 'Performer',
                type: 'table',
                subFields: [
                    {
                        id: 'artist',
                        header: 'Name of Artist ',
                        type: 'text',
                        rule: {
                            optionalRequired: true,
                            optionalRequiredMsg: '',
                            baseIds: [`${SECT_PERFORMER}-provide-performer-list`, `${SECT_PERFORMER}-performerListDocs`],
                            required: ({ baseValues }: RuleProps) => {
                                const result = Boolean(
                                    baseValues &&
                                        baseValues[`${SECT_PERFORMER}-provide-performer-list`] === 'Yes' &&
                                        ((baseValues[`${SECT_PERFORMER}-performerListDocs`] as Attachment[]) || []).filter(
                                            (file: Attachment) => !file.error && !file.deleted
                                        ).length === 0
                                );
                                return result;
                            }
                        }
                    },
                    {
                        id: 'group-band',
                        header: 'Group/Band Name',
                        type: 'text'
                    },
                    {
                        id: 'country',
                        header: 'Country',
                        apiID: 'Country',
                        type: 'dropdown',
                        rule: {
                            optionalRequired: true,
                            optionalRequiredMsg: '',
                            baseIds: [`${SECT_PERFORMER}-provide-performer-list`, `${SECT_PERFORMER}-performerListDocs`],
                            required: ({ baseValues }: RuleProps) => {
                                const result = Boolean(
                                    baseValues &&
                                        baseValues[`${SECT_PERFORMER}-provide-performer-list`] === 'Yes' &&
                                        ((baseValues[`${SECT_PERFORMER}-performerListDocs`] as Attachment[]) || []).filter(
                                            (file: Attachment) => !file.error && !file.deleted
                                        ).length === 0
                                );
                                return result;
                            }
                        }
                    }
                ],
                span: 12,
                buttonTitle: 'Add Performer',
                rule: {
                    optionalRequired: true,
                    optionalRequiredMsg: '',
                    revalidate: [`${SECT_PERFORMER}-performerListDocs`],
                    baseIds: [`${SECT_PERFORMER}-provide-performer-list`, `${SECT_PERFORMER}-performerListDocs`],
                    required: ({ baseValues }: RuleProps) => {
                        const validFiles = baseValues
                            ? ((baseValues[`${SECT_PERFORMER}-performerListDocs`] as Attachment[]) || []).filter(
                                  (item: Attachment) => !item.deleted && !item.error
                              )
                            : [];

                        const result = Boolean(
                            baseValues && baseValues[`${SECT_PERFORMER}-provide-performer-list`] === 'Yes' && !validFiles.length
                        );
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_PERFORMER}-provide-performer-list`] !== 'Yes');
                        return result;
                    }
                }
            },
            {
                id: 'performerListDocs',
                label: 'Upload Performer List',
                type: 'file',
                category: 'PerformerDetails',
                span: 12,
                rule: {
                    optionalRequired: true,
                    optionalRequiredMsg: '',
                    revalidate: [`${SECT_PERFORMER}-performer-list`],
                    baseIds: [`${SECT_PERFORMER}-provide-performer-list`, `${SECT_PERFORMER}-performer-list`],
                    required: ({ baseValues }: RuleProps) => {
                        const base =
                            baseValues &&
                            ((baseValues[`${SECT_PERFORMER}-performer-list`] as { [key: string]: FieldValue }[]) || []).filter(
                                (values: { [key: string]: FieldValue }) => !values.deleted
                            );
                        const result = Boolean(
                            baseValues && baseValues[`${SECT_PERFORMER}-provide-performer-list`] === 'Yes' && !(base || []).length
                        );
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_PERFORMER}-provide-performer-list`] !== 'Yes');
                        return result;
                    }
                },
                mimeTypes: [
                    'application/pdf',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                ],
                fileTypes: ['.pdf', '.xls', '.xlsx', '.doc', '.docx']
            }
        ]
    },
    {
        sectionID: SECT_DOCS,
        sectionName: 'Supporting Documents',
        fieldList: [
            {
                id: 'docs-list',
                type: 'helperList',
                span: 12,
                preField: {
                    id: 'link',
                    label: 'Link to Supporting Documents',
                    placeholder: 'Eg: https://yourdocumentlink.com/',
                    type: 'url'
                },
                mainField: {
                    id: 'docs',
                    label: 'Upload Documents',
                    type: 'file',
                    category: 'SupportingDocuments',
                    fileTypes: ['.jpeg', '.jpg', '.png', '.bmp', '.mp4', '.mpeg', '.mov', '.pdf', '.xls', '.doc', '.xlsx', '.docx'],
                    mimeTypes: [
                        'application/pdf',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        // 'text/plain',
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                        // 'image/gif',
                        'image/bmp',
                        'video/quicktime',
                        'video/mp4',
                        'video/mpeg'
                    ]
                },
                customList: SUPPORTING_DOCS,
                customListExpect: [`${SECT_EVENT_DETAILS}-event-type`, `${SECT_EVENT_DETAILS}-event-sub-type`]
            }
        ]
    }
];
